import { FC, ReactElement, useMemo } from 'react'
import classnames from 'classnames'

import AbsencesList from '../../../../../pages/Absences/components/Absence/AbsencesList'
import {
    filterResourceByDays,
    filterShiftsByDays,
    filterTracksByDays,
    getHeader,
} from '../../helpers'
import { DayBasis } from '../../models'
import {
    UnavailableSlotList,
    UserCalendarDayContent,
    UserCalendarDayHeader,
    UserShiftAndTracksList,
} from '../index'

import './UserCalendarDay.scss'

interface UserCalendarDayProps extends DayBasis {
    day: Date;
    index: number;
    last: boolean;
    outOfRange: boolean;
    showDate: boolean;
    onAbsenceMouseEnter: (id: string) => void;
    onAbsenceMouseLeave: (id: string) => void;
    className?: string;
}

const UserCalendarDay: FC<UserCalendarDayProps> = ({
    day,
    absences,
    leaveOfAbsences,
    shifts,
    specialDays,
    tracks,
    unavailableToWorkTimeSlots,
    showHeader,
    index,
    outOfRange,
    showDate,
    last,
    userId,
    onItemClick,
    onAbsenceMouseEnter,
    onAbsenceMouseLeave,
    className = '',
}): ReactElement => {
    const filteredAbsences = useMemo(() => filterResourceByDays(absences, day), [absences, day])
    const filteredLeaveOfAbsences = useMemo(() => filterResourceByDays(leaveOfAbsences, day), [leaveOfAbsences, day])
    const filteredShifts = useMemo(() => filterShiftsByDays(shifts, day), [shifts, day])
    const filteredTracks = useMemo(() => filterTracksByDays(tracks, day), [tracks, day])

    // Render leave first and sickness last so overlapping sickness bars stay clickable.
    const allFilteredAbsences = [...filteredLeaveOfAbsences, ...filteredAbsences]

    const header = getHeader(day)

    const dayClassName = classnames('day user-calendar-day', {
        'user-calendar-day--out-of-range': outOfRange,
    }, className)

    return (
        <div className={dayClassName} style={{ zIndex: (100 - index) }}>
            {showHeader && (
                <UserCalendarDayHeader header={header} />
            )}

            <UserCalendarDayContent
                day={day}
                specialDays={specialDays}
                showHeader={showDate}
            >
                {allFilteredAbsences.length > 0 && (
                    <AbsencesList
                        day={day}
                        dayIndex={index}
                        absences={allFilteredAbsences}
                        last={last}
                        filter={['date', 'type']}
                        onAbsenceClick={onItemClick}
                        onAbsenceMouseEnter={onAbsenceMouseEnter}
                        onAbsenceMouseLeave={onAbsenceMouseLeave}
                    />
                )}

                {!! (filteredShifts.length || filteredTracks.length) && (
                    <UserShiftAndTracksList
                        userId={userId}
                        shifts={filteredShifts}
                        tracks={filteredTracks}
                    />
                )}

                {unavailableToWorkTimeSlots.length > 0 && (
                    <UnavailableSlotList
                        day={day}
                        slots={unavailableToWorkTimeSlots}
                        onItemClick={onItemClick}
                    />
                )}
            </UserCalendarDayContent>
        </div>
    )
}

export default UserCalendarDay
